import React from 'react';
import { processSteps } from '../data/mock';
import { CheckCircle, ArrowRight } from 'lucide-react';

const ProcessSection = ({ onOpenForm }) => {
  return (
    <section id="process" className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-secondary font-semibold text-sm uppercase tracking-wide">Our Process</span>
            <h2 className="text-3xl md:text-4xl font-bold text-primary mt-2 mb-4">
              Simple 3-Step Trademark Registration Process
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto text-lg">
              We've streamlined the complex trademark registration process into three straightforward phases. From initial search to final registration, our expert team handles everything while keeping you informed every step of the way.
            </p>
          </div>

          {/* Process Steps */}
          <div className="space-y-8 mb-16">
            {processSteps.map((step, index) => (
              <div
                key={step.step}
                className="bg-white border-2 border-accent rounded-lg shadow-lg hover:border-secondary transition-all hover:shadow-xl overflow-hidden"
              >
                <div className="flex flex-col md:flex-row min-h-[320px]">
                  {/* Step Number */}
                  <div className="bg-gradient-to-br from-primary to-gray-900 text-white p-8 md:w-48 flex flex-col items-center justify-center flex-shrink-0">
                    <div className="bg-secondary w-20 h-20 rounded-full flex items-center justify-center text-3xl font-bold mb-4 shadow-lg">
                      {step.step}
                    </div>
                    <div className="text-center">
                      <div className="text-sm font-semibold uppercase tracking-wide">Step {step.step}</div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-8 flex flex-col">
                    <h3 className="text-2xl font-bold text-primary mb-4">{step.title}</h3>
                    <div className="flex-1 overflow-y-auto" style={{ maxHeight: '400px' }}>
                      <p className="text-gray-700 leading-relaxed text-base">{step.description}</p>
                    </div>
                    
                    {/* Key Points */}
                    <div className="mt-6 flex flex-wrap gap-3 flex-shrink-0">
                      {step.step === 1 && (
                        <>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Comprehensive Search
                          </span>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Expert Consultation
                          </span>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Risk Assessment
                          </span>
                        </>
                      )}
                      {step.step === 2 && (
                        <>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Professional Filing
                          </span>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            USPTO Monitoring
                          </span>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Office Action Response
                          </span>
                        </>
                      )}
                      {step.step === 3 && (
                        <>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Publication Monitoring
                          </span>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Registration Certificate
                          </span>
                          <span className="inline-flex items-center px-3 py-1 bg-accent/50 text-primary text-sm rounded-full">
                            <CheckCircle size={14} className="mr-1 text-secondary" />
                            Ongoing Support
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Timeline Info */}
          <div className="bg-gradient-to-br from-primary to-gray-900 text-white rounded-lg p-8 md:p-12 mb-12">
            <h3 className="text-2xl font-bold mb-8 text-center text-white">Complete Timeline Overview</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="bg-secondary/20 rounded-lg p-6 backdrop-blur-sm">
                  <div className="text-4xl font-bold text-secondary mb-2">2-3</div>
                  <div className="text-sm text-gray-300 mb-3">Business Days</div>
                  <div className="text-xs text-gray-400">Initial search and consultation phase</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-secondary/20 rounded-lg p-6 backdrop-blur-sm">
                  <div className="text-4xl font-bold text-secondary mb-2">8-12</div>
                  <div className="text-sm text-gray-300 mb-3">Months Average</div>
                  <div className="text-xs text-gray-400">Complete USPTO processing time</div>
                </div>
              </div>
              <div className="text-center">
                <div className="bg-secondary/20 rounded-lg p-6 backdrop-blur-sm">
                  <div className="text-4xl font-bold text-secondary mb-2">10+</div>
                  <div className="text-sm text-gray-300 mb-3">Years Protection</div>
                  <div className="text-xs text-gray-400">Initial protection period (renewable)</div>
                </div>
              </div>
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-300 text-sm max-w-3xl mx-auto">
                Timeline may vary based on USPTO workload and application complexity. We actively monitor your application and respond promptly to any examiner inquiries to minimize processing time. Most straightforward applications are approved within 8-10 months.
              </p>
            </div>
          </div>

          {/* Why Our Process Works */}
          <div className="bg-accent/30 rounded-lg p-8 md:p-12 mb-12">
            <h3 className="text-2xl font-bold text-primary mb-6 text-center">Why Our Streamlined Process Delivers Superior Results</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-start space-x-4">
                <div className="bg-secondary w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-2">Expert Legal Guidance</h4>
                  <p className="text-gray-700 text-sm">Every application is prepared and reviewed by experienced trademark attorneys who understand USPTO examination standards and trademark law nuances.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-secondary w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-2">Proactive Communication</h4>
                  <p className="text-gray-700 text-sm">We keep you informed at every stage with regular status updates, immediate notification of USPTO actions, and clear explanations of any issues that arise.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-secondary w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-2">Comprehensive Search</h4>
                  <p className="text-gray-700 text-sm">Our thorough initial search identifies potential conflicts before filing, preventing wasted time and money on applications likely to be refused.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="bg-secondary w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle className="text-white" size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-primary mb-2">Strategic Response</h4>
                  <p className="text-gray-700 text-sm">If office actions arise, our attorneys craft compelling legal arguments backed by case law and evidence to overcome objections and secure registration.</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-gray-600 mb-6 text-lg">
              Ready to begin protecting your brand with our proven 3-step process?
            </p>
            <button
              onClick={onOpenForm}
              className="bg-secondary text-white px-8 py-4 rounded-md hover:bg-secondary/90 transition-all font-semibold shadow-lg text-lg inline-flex items-center space-x-2"
            >
              <span>Start Your Registration Today</span>
              <ArrowRight size={20} />
            </button>
            <p className="text-sm text-gray-500 mt-4">
              Free consultation included • No obligation • Response within 24 hours
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
